// /opt/dashboard/frontend/src/pages/Reviews.js
// 📱 Értékelések — App Store + Google Play
import React, { useCallback, useEffect, useState } from 'react';

const C = {
  bg: '#1a1d27', panel: '#22252f', panel2: '#2a2e3a', border: '#2a2e3a',
  text: '#e0e6ed', muted: '#8892a0', accent: '#00d4ff',
  ok: '#22c55e', warn: '#fbbf24', bad: '#ef4444', purple: '#8b5cf6',
};

const Stars = ({ n }) => (
  <span style={{ color: n >= 4 ? C.ok : n === 3 ? C.warn : C.bad, letterSpacing: 1 }}>
    {'★'.repeat(n)}{'☆'.repeat(Math.max(0, 5 - n))}
  </span>
);

const storeIcon = (s) => (s === 'apple' ? '🍎' : '🤖');
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('hu-HU', { timeZone: 'Europe/Budapest', year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch (e) { return '—'; }
};

function SummaryCard({ icon, name, avg, count, extra }) {
  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 18px', flex: '1 1 220px', minWidth: 220 }}>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>{icon} {name}</div>
      <div style={{ fontSize: 28, fontWeight: 800 }}>
        {avg != null ? `⭐ ${avg}` : '—'}
        <span style={{ fontSize: 13, fontWeight: 400, color: C.muted, marginLeft: 8 }}>
          {count != null ? `${count} értékelés` : 'nincs adat'}
        </span>
      </div>
      {extra}
    </div>
  );
}

export default function Reviews() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [storeFilter, setStoreFilter] = useState('all'); // all | apple | google
  const [criticalOnly, setCriticalOnly] = useState(false);

  const load = useCallback((force) => {
    if (force) setRefreshing(true); else setLoading(true);
    setError(null);
    fetch('/api/reviews' + (force ? '?refresh=1' : ''))
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((j) => { setData(j); setLoading(false); setRefreshing(false); })
      .catch((e) => { setError(e.message); setLoading(false); setRefreshing(false); });
  }, []);

  useEffect(() => { load(false); }, [load]);

  const reviews = (data?.reviews || []).filter((r) =>
    (storeFilter === 'all' || r.store === storeFilter) && (!criticalOnly || r.rating <= 3));

  const chip = (active) => ({
    padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, userSelect: 'none',
    background: active ? C.accent : C.panel,
    border: `1px solid ${active ? C.accent : C.border}`,
    color: active ? '#0a0e1a' : C.text, fontWeight: active ? 700 : 400,
  });

  return (
    <div style={{ color: C.text, maxWidth: 900 }}>
      <h2 style={{ margin: '0 0 4px' }}>📱 Értékelések</h2>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
        App Store és Google Play — globális, 6 óránként frissülő cache
        {data?.updatedAt ? ` · frissítve: ${new Date(data.updatedAt).toLocaleString('hu-HU', { timeZone: 'Europe/Budapest' })}` : ''}
      </div>

      {loading && <div style={{ color: C.muted, padding: 20 }}>⏳ Betöltés…</div>}
      {error && (
        <div style={{ background: C.panel, border: `1px solid ${C.bad}`, borderRadius: 12, padding: 16, color: C.bad, marginBottom: 16 }}>
          ❌ Hiba: {error}
        </div>
      )}

      {!loading && data && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <SummaryCard
              icon="🍎" name="App Store (súlyozott átlag)"
              avg={data.apple?.avg} count={data.apple?.total || null}
              extra={data.apple?.perCountry?.length > 0 && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                  {data.apple.perCountry.slice(0, 6).map((c) => `${c.country}: ⭐${c.avg} (${c.count})`).join(' · ')}
                </div>
              )}
            />
            <SummaryCard icon="🤖" name="Google Play" avg={data.google?.score} count={data.google?.ratings} />
          </div>

          {data.errors?.length > 0 && (
            <div style={{ fontSize: 12, color: C.warn, marginBottom: 12 }}>
              ⚠️ Részleges adat: {data.errors.join(' · ')}
            </div>
          )}

          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={chip(storeFilter === 'all')} onClick={() => setStoreFilter('all')}>Mind ({data.reviews?.length || 0})</span>
            <span style={chip(storeFilter === 'apple')} onClick={() => setStoreFilter('apple')}>🍎 App Store</span>
            <span style={chip(storeFilter === 'google')} onClick={() => setStoreFilter('google')}>🤖 Google Play</span>
            <span style={chip(criticalOnly)} onClick={() => setCriticalOnly(!criticalOnly)}>⚠️ Csak 1–3★</span>
            <span style={{ flex: 1 }} />
            <button
              onClick={() => load(true)} disabled={refreshing}
              style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '7px 14px', fontSize: 13, cursor: 'pointer', opacity: refreshing ? 0.5 : 1 }}
            >
              {refreshing ? '⏳ Frissítés…' : '🔄 Frissítés most'}
            </button>
          </div>

          {reviews.length === 0 && (
            <div style={{ color: C.muted, padding: 20, background: C.panel, borderRadius: 12, border: `1px solid ${C.border}` }}>
              Nincs megjeleníthető értékelés{criticalOnly || storeFilter !== 'all' ? ' ezzel a szűrővel' : ' még'}.
            </div>
          )}

          {reviews.map((r) => (
            <div key={r.id} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span>{storeIcon(r.store)}</span>
                <Stars n={r.rating} />
                {r.title && <strong style={{ fontSize: 14 }}>{r.title}</strong>}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 12, color: C.muted }}>
                  {r.author || 'névtelen'} · {r.region}{r.version ? ` · v${r.version}` : ''} · {fmtDate(r.date)}
                </span>
              </div>
              {r.text && <div style={{ fontSize: 13, color: C.text, marginTop: 8, lineHeight: 1.5 }}>{r.text}</div>}
            </div>
          ))}
        </>
      )}
      <div style={{ height: 40 }} />
    </div>
  );
}
